/**
 * AppointmentTab Component
 * Handles appointment booking and viewing for lab orders
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { PatientLabOrder } from '@/services/patientLabService';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';

interface AppointmentTabProps {
  order: PatientLabOrder;
  sessionToken: string;
  onBooked: () => void;
}

const AppointmentTab: React.FC<AppointmentTabProps> = ({
  order,
  sessionToken,
  onBooked,
}) => {
  const { toast } = useToast();
  const [isBooking, setIsBooking] = useState(false);

  if (order.collectionMethod === 'walk_in') {
    return (
      <div className="space-y-4">
        <Card className="border-slate-700 bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-400" />
              Walk-in Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-slate-300 mb-2">
                For walk-in orders, you can visit any Quest Diagnostics location.
              </p>
              <p className="text-sm text-slate-400">
                Use the Lab Centers tab to find a convenient location near you.
              </p>
            </div>

            {order.requisitionUrl && (
              <div className="pt-4 border-t border-slate-700">
                <p className="text-sm font-medium mb-2">Requisition Form</p>
                <a
                  href={order.requisitionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 underline text-sm"
                >
                  Download Requisition PDF
                </a>
              </div>
            )}

            <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-300">
                <strong>Important:</strong> Bring a valid ID and your requisition form
                when visiting the lab.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // At-home phlebotomy
  const hasAppointment = order.status === 'appointment_scheduled';

  return (
    <div className="space-y-4">
      {hasAppointment ? (
        <Card className="border-slate-700 bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-400" />
              Appointment Scheduled
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-slate-300">
                  {order.metadata?.appointmentDate
                    ? format(
                        parseISO(order.metadata.appointmentDate as string),
                        'EEEE, MMMM d, yyyy'
                      )
                    : 'Date scheduled'}
                </span>
              </div>
              {order.metadata?.appointmentTime && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-300">
                    {order.metadata.appointmentTime as string}
                  </span>
                </div>
              )}
              {order.patientAddress && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div className="text-sm text-slate-300">
                    <p>{order.patientAddress.firstLine}</p>
                    {order.patientAddress.city && order.patientAddress.state && (
                      <p>
                        {order.patientAddress.city}, {order.patientAddress.state}{' '}
                        {order.patientAddress.zip}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-green-900/20 border border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-300">
                A phlebotomist will arrive at your address at the scheduled time.
                Please be available.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-700 bg-slate-800">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-400" />
              Schedule Appointment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-300">
              To schedule your at-home phlebotomy appointment, please contact your
              clinic or use the booking system if available.
            </p>
            <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-300">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                Appointment booking integration coming soon. Please contact your
                clinic to schedule.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AppointmentTab;
